import moment from 'moment';

const FIXED_PRICE = 'pricesFixedForm';
const WEEKLY_PRICE = 'pricesWeeklyForm';
const DAILY_PRICE = 'pricesDailyForm';
const HOURLY_PRICE = 'pricesHourlyForm';
const WEEKDAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI'];
const WEEKENDS = ['SAT', 'SUN'];

const getAvailableUnits = (
  rentalItems,
  bookRentalItemsForm,
  currency,
  previousAvailableUnits
) => {
  if (!rentalItems) return;

  const selectedCurrency = currency || bookRentalItemsForm.values.currency;
  let startingDate = bookRentalItemsForm.values.startingDate;
  const multiDates = bookRentalItemsForm.values.multiDates;
  const startTime = bookRentalItemsForm.values.startTime;
  const endTime = bookRentalItemsForm.values.endTime;
  const prices = rentalItems[rentalItems.pricesType];
  const pricesType = rentalItems['pricesType'];

  const rentalWeekDays =
    rentalItems.pricesWeeklyForm?.weekList.flatMap(item => item.days) || [];

  const availableUnits = [];

  if (!startingDate && multiDates) {
    startingDate = multiDates.startDate;
  }

  if (pricesType === HOURLY_PRICE && startTime && endTime) {
    _setHourlyAvailableUnits(
      prices,
      availableUnits,
      selectedCurrency,
      startTime,
      endTime,
      previousAvailableUnits,
      pricesType
    );
 
    
    return availableUnits;
  }

  if (multiDates) {
    const selectedDays = getDaysBetweenDates(
      multiDates.startDate,
      multiDates.endDate
    );

    const hasMatchingDays = selectedDays.some(day =>
      rentalWeekDays.includes(day)
    );

    if (pricesType === FIXED_PRICE) {
      _setFixedAvailableUnits(
        prices,
        availableUnits,
        selectedCurrency,
        previousAvailableUnits,
        pricesType,
        multiDates 
      );
      return availableUnits;
    }

    if (pricesType === WEEKLY_PRICE && hasMatchingDays) {
      const mixedDayResult = _handleMixedDaySelection(
        prices,
        availableUnits,
        selectedCurrency,
        selectedDays,
        previousAvailableUnits,
        pricesType
      );

      if (mixedDayResult.hasMixedTypes) {
        if (mixedDayResult.combinedUnit) {
          availableUnits.length = 0;
          availableUnits.push(mixedDayResult.combinedUnit);
        }
        return availableUnits;
      } else {
        _setWeeklyAvailableUnits(
          prices,
          availableUnits,
          selectedCurrency,
          selectedDays,
          previousAvailableUnits,
          pricesType
        );
      }
      return availableUnits;
    }

    if (pricesType === WEEKLY_PRICE && !hasMatchingDays) {
      return availableUnits;
    }

    if (pricesType === DAILY_PRICE && multiDates) {
      const selectedDays = getDaysBetweenDates(
        multiDates.startDate,
        multiDates.endDate
      );

      _setDailyAvailableUnits(
        prices,
        availableUnits,
        selectedCurrency,
        selectedDays,
        previousAvailableUnits,
        pricesType
      );
      return availableUnits;
    }
  }

  if (pricesType === FIXED_PRICE) {
    const item = prices;
    if (item) {
      _setFixedAvailableUnits(
        item,
        availableUnits,
        selectedCurrency,
        previousAvailableUnits,
        pricesType,
        multiDates 
      );
    }
  }

  if (pricesType === WEEKLY_PRICE) {
    const item = prices;
    if (item) {
      const singleDay = startingDate
        ? moment(startingDate)
            .format('ddd')
            .toUpperCase()
        : null;
      if (singleDay && rentalWeekDays.includes(singleDay)) {
        _setWeeklyAvailableUnits(
          item,
          availableUnits,
          selectedCurrency,
          [singleDay],
          previousAvailableUnits,
          pricesType
        );
      }
    }
  }

  if (pricesType === DAILY_PRICE && multiDates) {
    const selectedDays = getDaysBetweenDates(
      multiDates.startDate,
      multiDates.endDate
    );

    _setDailyAvailableUnits(
      prices,
      availableUnits,
      selectedCurrency,
      selectedDays,
      previousAvailableUnits,
      pricesType
    );
    return availableUnits;
  }

  if (pricesType === DAILY_PRICE && startingDate) {
    const singleDay = moment(startingDate)
      .format('ddd')
      .toUpperCase();
    _setDailyAvailableUnits(
      prices,
      availableUnits,
      selectedCurrency,
      [singleDay],
      previousAvailableUnits,
      pricesType
    );
    return availableUnits;
  }

  if (pricesType === HOURLY_PRICE && startingDate && startTime && endTime) {
    _setHourlyAvailableUnits(
      prices,
      availableUnits,
      selectedCurrency,
      startTime,
      endTime,
      previousAvailableUnits,
      pricesType
    );
    return availableUnits;
  }

  return availableUnits;
};

const _setDailyAvailableUnits = (
  dailyPrices,
  availableUnits,
  selectedCurrency,
  selectedDays,
  previousAvailableUnits,
  pricesType
) => {
  if (!dailyPrices || !dailyPrices.days || !selectedDays.length) return;

  let adjustedTotalPrice = 0;
  let minAvailableUnits = Infinity;
  const breakdown = [];

  selectedDays.forEach(day => {
    const dailyItem = dailyPrices.days.find(d => d.day.toUpperCase() === day);

    if (dailyItem) {
      const value = parseFloat(dailyItem[selectedCurrency] || 0);
      adjustedTotalPrice += value;

      minAvailableUnits = Math.min(
        minAvailableUnits,
        dailyItem.availableUnits || 0
      );

      breakdown.push({
        day: dailyItem.day,
        value,
        currency: selectedCurrency,
        pricesType : pricesType
      });
    }
  });

  const dailyUnit = {
    id: `daily-${selectedCurrency}-${adjustedTotalPrice}`,
    availableUnits: isFinite(minAvailableUnits) ? minAvailableUnits : 0,
    currency: selectedCurrency,
    currencyValue: adjustedTotalPrice,
    days: selectedDays.length,
    type: 'daily',
    USD: adjustedTotalPrice,
    EUR: adjustedTotalPrice,
    NOK: adjustedTotalPrice,
    breakdown
  };


  const updatedUnits = _setPreviousAvailableValue(
    [dailyUnit],
    previousAvailableUnits
  );

  availableUnits.length = 0;
  availableUnits.push(...updatedUnits);
};



const _handleMixedDaySelection = (
  weeklyPrices,
  availableUnits,
  selectedCurrency,
  selectedDays,
  previousAvailableUnits,
  pricesType
) => {
  if (!weeklyPrices || !weeklyPrices.weekList) {
    return { hasMixedTypes: false, individualUnits: [], combinedUnit: null };
  }

  const individualUnits = [];
  _setWeeklyAvailableUnits(
    weeklyPrices,
    individualUnits,
    selectedCurrency,
    selectedDays,
    previousAvailableUnits,
    pricesType
  );

  const uniqueTypes = [...new Set(individualUnits.map(unit => unit.type))];
  const hasMixedTypes = uniqueTypes.length > 1;

  if (!hasMixedTypes) {
    return { hasMixedTypes: false, individualUnits, combinedUnit: null };
  }

  const combinedUnit = _createCombinedMixedDaysUnit(
    weeklyPrices,
    selectedCurrency,
    selectedDays,
    previousAvailableUnits,
    pricesType
  );

  return { hasMixedTypes: true, individualUnits, combinedUnit };
};

const _createCombinedMixedDaysUnit = (
  prices,
  selectedCurrency,
  selectedDays,
  previousAvailableUnits,
  pricesType
) => {
  if (!prices?.weekList || !selectedDays.length) return null;

  const weekdayItem = prices.weekList.find(item => item.type === 'weekday');
  const weekendItem = prices.weekList.find(item => item.type === 'weekend');

  const weekdayRate = weekdayItem
    ? parseFloat(weekdayItem[selectedCurrency]) || 0
    : 0;
  const weekendRate = weekendItem
    ? parseFloat(weekendItem[selectedCurrency]) || 0
    : 0;

  const selectedWeekdays = selectedDays.filter(day => WEEKDAYS.includes(day));
  const selectedWeekends = selectedDays.filter(day => WEEKENDS.includes(day));

  const weekdayTotal = weekdayRate * selectedWeekdays.length;
  const weekendTotal = weekendRate * selectedWeekends.length;
  const totalPrice = weekdayTotal + weekendTotal;

  const combinedUnit = {
    id: `combined-${selectedCurrency}-${totalPrice}`,
    availableUnits: Math.min(
      weekdayItem?.availableUnits || 0,
      weekendItem?.availableUnits || 0
    ),
    currency: selectedCurrency,
    currencyValue: totalPrice,
    NOK:
      weekdayItem?.NOK && weekendItem?.NOK
        ? parseFloat(weekdayItem.NOK) * selectedWeekdays.length +
          parseFloat(weekendItem.NOK) * selectedWeekends.length
        : null,
    EUR:
      weekdayItem?.EUR && weekendItem?.EUR
        ? parseFloat(weekdayItem.EUR) * selectedWeekdays.length +
          parseFloat(weekendItem.EUR) * selectedWeekends.length
        : null,
    USD:
      weekdayItem?.USD && weekendItem?.USD
        ? parseFloat(weekdayItem.USD) * selectedWeekdays.length +
          parseFloat(weekendItem.USD) * selectedWeekends.length
        : null,
    days: selectedDays.length,
    type: 'combinedMixed',
    isCombined: true,
    breakdown: [
      {
        type: 'weekday',
        days: selectedWeekdays.length,
        perDayPrice: weekdayRate,
        total: weekdayTotal,
        availableUnits: weekdayItem?.availableUnits || 0,
        pricesType: pricesType
      },
      {
        type: 'weekend',
        days: selectedWeekends.length,
        perDayPrice: weekendRate,
        total: weekendTotal,
        availableUnits: weekendItem?.availableUnits || 0,
        pricesType: pricesType
      }
    ]
  };

  const updatedUnits = _setPreviousAvailableValue(
    [combinedUnit],
    previousAvailableUnits
  );
  return updatedUnits[0];
};
function getDaysBetweenDates(startDate, endDate) {
  if (!startDate || !endDate) {
    return [];
  }

  const days = [];
  let currentDate;
  let end;

  if (moment.isMoment(startDate)) {
    currentDate = startDate.clone();
  } else {
    currentDate = moment(startDate);
  }

  if (moment.isMoment(endDate)) {
    end = endDate.clone();
  } else {
    end = moment(endDate);
  }

  while (currentDate.isSameOrBefore(end, 'day')) {
    const dayName = currentDate.format('ddd').toUpperCase();
    days.push(dayName);
    currentDate.add(1, 'day');
  }

  return days;
}

const _setFixedAvailableUnits = (
  prices,
  availableUnits,
  currency,
  previousAvailableUnits,
  pricesType,
  multiDates
) => {

  let newAvailableUnits = [];

  let totalDays = 1;
  if (multiDates?.startDate && multiDates?.endDate) {
    const start = moment(multiDates.startDate);
    const end = moment(multiDates.endDate);
    totalDays = end.diff(start, "days") + 1; 
  }

  const multipliedPrice = (prices[currency] || 0) * totalDays;

  newAvailableUnits.push({
    id: `${currency}-${multipliedPrice}`,
    availableUnits: 0,
    currency,
    currencyValue: multipliedPrice,
    NOK: (prices.NOK || 0) * totalDays,
    EUR: (prices.EUR || 0) * totalDays,
    USD: (prices.USD || 0) * totalDays,
    pricesType,
    days: totalDays
  });


  const updatedAvailableUnits = _setPreviousAvailableValue(
    newAvailableUnits,
    previousAvailableUnits
  ); 
  availableUnits.length = 0;
  availableUnits.push(...updatedAvailableUnits);
  return availableUnits;
};

const _setWeeklyAvailableUnits = (
  prices,
  availableUnits,
  selectedCurrency,
  selectedDays,
  previousAvailableUnits,
  pricesType
) => {
  
  if (!prices?.weekList || !selectedDays.length) return;

  // Calculate price for each category
  prices.weekList.forEach(weekItem => {
    const selectedInCategory = selectedDays.filter(day =>
      weekItem.days.includes(day)
    ).length;

    if (selectedInCategory > 0) {
      const rate = parseFloat(weekItem[selectedCurrency]) || 0;
      const totalPrice = rate * selectedInCategory;

      const unit = {
        id: `${weekItem.type}-${selectedCurrency}-${totalPrice}`,
        availableUnits: weekItem.availableUnits || 0,
        currency: selectedCurrency,
        currencyValue: totalPrice,
        NOK: weekItem.NOK
          ? parseFloat(weekItem.NOK) * selectedInCategory
          : null,
        EUR: weekItem.EUR
          ? parseFloat(weekItem.EUR) * selectedInCategory
          : null,
        USD: weekItem.USD
          ? parseFloat(weekItem.USD) * selectedInCategory
          : null,
        days: selectedInCategory,
        type: weekItem.type,
        perDayPrice: rate,
        totalDaysInCategory: weekItem.days.length,
        pricesType: pricesType
      };

      const updatedUnit = _setPreviousAvailableValue(
        [unit],
        previousAvailableUnits
      )[0];
      availableUnits.push(updatedUnit);
    }
  });

  return availableUnits;
};

const _setHourlyAvailableUnits = (
  hourlyPrices,
  availableUnits,
  selectedCurrency,
  startTime,
  endTime,
  previousAvailableUnits,
  pricesType,
  includeEndHour = true 
) => {  
  if (!hourlyPrices?.hourlyList || !startTime || !endTime) {
    availableUnits.length = 0;
    return;
  }

 
  
  const startMoment = moment.isMoment(startTime) ? startTime : moment(startTime);
  const endMoment = moment.isMoment(endTime) ? endTime : moment(endTime);

  if (!startMoment.isValid() || !endMoment.isValid()) {
    availableUnits.length = 0;
    return;
  }

  const durationHours = endMoment.diff(startMoment, "hours", true);

  if (durationHours <= 0) {
    availableUnits.length = 0;
    return;
  }

  let totalPrice = 0;
  const breakdown = [];

  const masterPriceItem = hourlyPrices.hourlyList.find(item => item.isMaster);
  const masterPrice = parseFloat(masterPriceItem?.[selectedCurrency] || 0);
  
  const hourlyRates = hourlyPrices.hourlyList
    .filter(item => !item.isMaster && item.startTime)
    .sort((a, b) => a.startTime - b.startTime);

  const loopStart = startMoment.clone().startOf("hour");
  let loopEnd = endMoment.clone().startOf("hour");
  
  if (includeEndHour || endMoment.minutes() > 0) {
    loopEnd = loopEnd.add(1, "hour");
  }

  for (
    let currentHour = loopStart.clone();
    currentHour.isBefore(loopEnd);
    currentHour.add(1, "hour")
  ) {
    const hourString = currentHour.format("HH:mm");
    
    const hourlyItem = hourlyRates.find((item) => {
      return item.startTime?.format("HH:mm") === hourString;
    });

    if (hourlyItem) {
      const value = parseFloat(hourlyItem[selectedCurrency] || 0);
      totalPrice += value;

      breakdown.push({
        hour: hourString,
        value,
        currency: selectedCurrency,
        timestamp: currentHour.format(),
        pricesType: pricesType,
        hourlyItemId: hourlyItem.id,
        rateType: 'hourly'
      });
    } else {
      totalPrice += masterPrice;
      
      breakdown.push({
        hour: hourString,
        value: masterPrice,
        currency: selectedCurrency,
        timestamp: currentHour.format(),
        pricesType: pricesType,
        rateType: 'master',
        usedMasterPrice: true
      });
    }
  }

  if (breakdown.length === 0) {
    availableUnits.length = 0;
    return;
  }

  const hourlyUnit = {
    id: `hourly-${selectedCurrency}-${totalPrice}-${startMoment.format()}-${endMoment.format()}`,
    availableUnits: 0,
    currency: selectedCurrency,
    currencyValue: totalPrice,
    hours: durationHours,
    type: 'hourly',
    startTime: startMoment.format(),
    endTime: endMoment.format(),
    USD: totalPrice,
    EUR: totalPrice,
    NOK: totalPrice,
    breakdown,
    calculatedAt: new Date().toISOString(),
    masterPriceUsed: breakdown.some(item => item.usedMasterPrice)
  };

  const updatedUnits = _setPreviousAvailableValue(
    [hourlyUnit],
    previousAvailableUnits
  );

  availableUnits.length = 0;
  availableUnits.push(...updatedUnits);
};
const _setPreviousAvailableValue = (availableUnits, previousAvailableUnits) => {

  
  if (typeof previousAvailableUnits === "number") {
    return availableUnits.map(item => ({
      ...item,
      availableUnits: previousAvailableUnits
    }));
  }

  return availableUnits;
};

export  {getAvailableUnits};
